// src/components/ZoomRegionEditor.tsx
import { useState, useRef, useCallback } from 'react';
import { ZoomRegion } from '../types/capsule';

type EditingMode = 'rightHand' | 'leftHand' | null;

interface ZoomRegionEditorProps {
  videoElement: HTMLVideoElement | null;
  rightHandRegion?: ZoomRegion;
  leftHandRegion?: ZoomRegion;
  onRegionChange: (hand: 'rightHand' | 'leftHand', region: ZoomRegion | undefined) => void;
  onTestZoom: (region: ZoomRegion) => void;
  onResetZoom: () => void;
  /** External control of editing mode */
  editingMode?: EditingMode;
  onEditingModeChange?: (mode: EditingMode) => void;
  /** Hide built-in controls (for external control UI) */
  hideControls?: boolean;
}

export function ZoomRegionEditor({
  videoElement,
  rightHandRegion,
  leftHandRegion,
  onRegionChange,
  onTestZoom,
  onResetZoom,
  editingMode: externalEditingMode,
  onEditingModeChange,
  hideControls = false,
}: ZoomRegionEditorProps) {
  const [internalEditingMode, setInternalEditingMode] = useState<EditingMode>(null);

  // Use external editing mode if provided, otherwise use internal
  const editingMode = externalEditingMode !== undefined ? externalEditingMode : internalEditingMode;
  const setEditingMode = onEditingModeChange || setInternalEditingMode;
  const [drawingRegion, setDrawingRegion] = useState<ZoomRegion | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const startPointRef = useRef<{ x: number; y: number } | null>(null);

  const getPercentCoords = useCallback((e: React.MouseEvent) => {
    const overlay = overlayRef.current;
    if (!overlay) return { x: 0, y: 0 };

    const rect = overlay.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!editingMode) return;

    const coords = getPercentCoords(e);
    startPointRef.current = coords;
    setIsDrawing(true);
    setDrawingRegion({ x: coords.x, y: coords.y, width: 0, height: 0 });
  }, [editingMode, getPercentCoords]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDrawing || !startPointRef.current) return;

    const coords = getPercentCoords(e);
    const start = startPointRef.current;

    const x = Math.min(start.x, coords.x);
    const y = Math.min(start.y, coords.y);
    const width = Math.abs(coords.x - start.x);
    const height = Math.abs(coords.y - start.y);

    setDrawingRegion({ x, y, width, height });
  }, [isDrawing, getPercentCoords]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !drawingRegion || !editingMode) return;

    // Only save if region has meaningful size
    if (drawingRegion.width > 5 && drawingRegion.height > 5) {
      onRegionChange(editingMode, drawingRegion);
    }

    setIsDrawing(false);
    setDrawingRegion(null);
    startPointRef.current = null;
  }, [isDrawing, drawingRegion, editingMode, onRegionChange]);

  const handleStartEdit = (mode: EditingMode) => {
    setEditingMode(mode);
    setDrawingRegion(null);
    if (videoElement) {
      videoElement.pause();
    }
  };

  const handleDoneEdit = () => {
    setEditingMode(null);
    setDrawingRegion(null);
    onResetZoom();
  };

  const handleClear = () => {
    if (editingMode) {
      onRegionChange(editingMode, undefined);
      setDrawingRegion(null);
    }
  };

  const handleTest = () => {
    const region = editingMode === 'rightHand' ? rightHandRegion : leftHandRegion;
    if (region) {
      onTestZoom(region);
    }
  };

  const currentRegion = editingMode === 'rightHand' ? rightHandRegion : leftHandRegion;

  return (
    <div className="zoom-region-editor">
      {/* Overlay for drawing (only shown when editing) */}
      {editingMode && (
        <div
          ref={overlayRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            cursor: 'crosshair',
            zIndex: 10,
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Draw the region rectangle */}
          {(drawingRegion || currentRegion) && (
            <div
              className="zoom-selection-rect"
              style={{
                left: `${(drawingRegion || currentRegion)!.x}%`,
                top: `${(drawingRegion || currentRegion)!.y}%`,
                width: `${(drawingRegion || currentRegion)!.width}%`,
                height: `${(drawingRegion || currentRegion)!.height}%`,
              }}
            />
          )}
        </div>
      )}

      {/* Controls - can be hidden when using external controls */}
      {!hideControls && (
        <div className="zoom-region-controls">
          <div className="zoom-region-cards">
            <div className={`zoom-region-card ${editingMode === 'rightHand' ? 'editing' : ''}`}>
              <div className="zoom-region-card-header">
                <span>Right Hand</span>
                {rightHandRegion ? (
                  <span className="region-status defined">&#10003; Defined</span>
                ) : (
                  <span className="region-status default">&#9675; Using default</span>
                )}
              </div>
              {editingMode !== 'rightHand' ? (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleStartEdit('rightHand')}
                >
                  Edit Region
                </button>
              ) : (
                <div className="edit-actions">
                  <button className="btn btn-secondary" onClick={handleTest} disabled={!currentRegion}>
                    Test Zoom
                  </button>
                  <button className="btn btn-secondary" onClick={handleClear}>
                    Clear
                  </button>
                  <button className="btn btn-primary" onClick={handleDoneEdit}>
                    Done
                  </button>
                </div>
              )}
            </div>

            <div className={`zoom-region-card ${editingMode === 'leftHand' ? 'editing' : ''}`}>
              <div className="zoom-region-card-header">
                <span>Left Hand</span>
                {leftHandRegion ? (
                  <span className="region-status defined">&#10003; Defined</span>
                ) : (
                  <span className="region-status default">&#9675; Using default</span>
                )}
              </div>
              {editingMode !== 'leftHand' ? (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleStartEdit('leftHand')}
                >
                  Edit Region
                </button>
              ) : (
                <div className="edit-actions">
                  <button className="btn btn-secondary" onClick={handleTest} disabled={!currentRegion}>
                    Test Zoom
                  </button>
                  <button className="btn btn-secondary" onClick={handleClear}>
                    Clear
                  </button>
                  <button className="btn btn-primary" onClick={handleDoneEdit}>
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
